import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Modal,
  Form,
  Input,
  Select,
  Tooltip,
  Empty,
  Switch,
  Alert,
  Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import {
  getUserCloudProviders,
  createCloudProvider,
  updateCloudProvider,
  deleteCloudProvider,
  getCloudCatalogProviders,
  getPlatformSettings,
} from '../services/api';
import { getDataCollectionsTablePagination } from '../components/collections/collectionUtils';
import './DataCollections.css';

const { Text } = Typography;

function Vault() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { keycloak, initialized } = useKeycloak();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [form] = Form.useForm();
  const [catalogProviders, setCatalogProviders] = useState([]);
  const [cloudModelsEnabled, setCloudModelsEnabled] = useState(true);

  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      fetchProviders();
      fetchCatalogProviders();
      fetchPlatformSettings();
    }
  }, [initialized, keycloak]);

  useEffect(() => {
    if (!initialized || !keycloak?.authenticated) return;
    if (searchParams.get('create') !== '1') return;
    setEditingProvider(null);
    form.resetFields();
    setModalVisible(true);
    navigate('/vault', { replace: true });
  }, [initialized, keycloak?.authenticated, searchParams, navigate, form]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const data = await getUserCloudProviders(keycloak);
      setProviders(data || []);
    } catch (err) {
      message.error(`Failed to load API keys: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogProviders = async () => {
    try {
      const data = await getCloudCatalogProviders(keycloak);
      setCatalogProviders(data || []);
    } catch (err) {
      console.error('Failed to load catalog providers:', err);
    }
  };

  const fetchPlatformSettings = async () => {
    try {
      const settings = await getPlatformSettings(keycloak);
      setCloudModelsEnabled(settings.cloud_models_enabled || false);
    } catch (err) {
      // If user is not admin, settings endpoint will fail - default to enabled
      console.error('Failed to load platform settings:', err);
      setCloudModelsEnabled(true);
    }
  };

  const handleAdd = () => {
    setEditingProvider(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingProvider(record);
    form.setFieldsValue({
      name: record.name,
      provider: record.provider,
      api_key: '', // Don't show existing key
      api_base: record.api_base || '',
      is_active: record.is_active,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCloudProvider(keycloak, id);
      message.success('API key deleted successfully');
      fetchProviders();
    } catch (err) {
      message.error(`Failed to delete API key: ${err.message}`);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingProvider) {
        await updateCloudProvider(keycloak, editingProvider.id, values);
        message.success('API key updated successfully');
      } else {
        await createCloudProvider(keycloak, values);
        message.success('API key added successfully');
      }
      setModalVisible(false);
      form.resetFields();
      fetchProviders();
    } catch (err) {
      message.error(`Failed to ${editingProvider ? 'update' : 'create'} API key: ${err.message}`);
    }
  };

  const maskApiKeyLastFour = (preview) => {
    if (!preview) return '—';
    const s = String(preview);
    if (s.length <= 4) return '•'.repeat(4);
    return `${'•'.repeat(s.length - 4)}${s.slice(-4)}`;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Text strong>{text}</Text>
          {!record.is_active && <Tag color="default">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider) => {
        const catalogProvider = catalogProviders.find((p) => p.litellm_provider === provider);
        return (
          <Space>
            {catalogProvider?.icon && (
              <img src={catalogProvider.icon} alt={provider} style={{ width: 20, height: 20 }} />
            )}
            <Text>{catalogProvider?.name || provider}</Text>
          </Space>
        );
      },
    },
    {
      title: 'API Key',
      dataIndex: 'api_key_preview',
      key: 'api_key',
      render: (preview) => (
        <Text code style={{ fontFamily: 'monospace' }}>
          {maskApiKeyLastFour(preview)}
        </Text>
      ),
    },
    {
      title: 'API Base',
      dataIndex: 'api_base',
      key: 'api_base',
      render: (base) => (base ? <Text type="secondary" code>{base}</Text> : <Text type="secondary">Default</Text>),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>{isActive ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '-'),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 88,
      align: 'center',
      className: 'actions-cell',
      render: (_, record) => (
        <Space
          size={4}
          className="collections-list-actions-toolbar assistants-actions-toolbar"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              aria-label="Edit"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEdit(record);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete API key"
            description="This action cannot be undone."
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(record.id);
            }}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Tooltip title="Delete">
              <Button
                danger
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                aria-label="Delete"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="collections-page-header">
        <div className="page-header" style={{ flex: 1, minWidth: 0 }}>
          <h2 className="page-header-title">Vault</h2>
          <span className="page-header-subtitle">Manage your cloud provider API keys</span>
        </div>
        <Tooltip title={!cloudModelsEnabled ? 'Cloud models are disabled in platform settings' : ''}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="new-collection-btn"
            onClick={handleAdd}
            disabled={!cloudModelsEnabled}
          >
            Add API Key
          </Button>
        </Tooltip>
      </div>

      {!cloudModelsEnabled && (
        <Alert
          message="Cloud Models Disabled"
          description="Cloud provider API keys cannot be created because cloud models are disabled in platform settings."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="collections-table-wrapper collections-table-animate">
        <Table
          columns={columns}
          dataSource={providers}
          loading={loading}
          rowKey="id"
          pagination={getDataCollectionsTablePagination(10, { totalItemLabel: 'keys' })}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                description={cloudModelsEnabled ? 'No API keys configured' : 'Cloud models are disabled'}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                {cloudModelsEnabled && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    Add Your First API Key
                  </Button>
                )}
              </Empty>
            ),
          }}
        />
      </div>

      <Modal
        title={editingProvider ? 'Edit API Key' : 'Add API Key'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={editingProvider ? 'Update' : 'Add'}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name for this API key' }]}
          >
            <Input placeholder="e.g., My OpenAI Key" />
          </Form.Item>

          <Form.Item
            name="provider"
            label="Provider"
            rules={[{ required: true, message: 'Please select a provider' }]}
          >
            <Select
              placeholder="Select a cloud provider"
              showSearch
              filterOption={(input, option) => {
                try {
                  // Filter by provider name (from catalog) or value
                  const providerValue = option?.value || '';
                  const provider = catalogProviders.find(p => p.litellm_provider === providerValue);
                  const searchText = (provider?.name || providerValue || '').toString();
                  const inputText = (input || '').toString();
                  return searchText.toLowerCase().includes(inputText.toLowerCase());
                } catch (e) {
                  // Fallback: just check if value matches
                  const value = (option?.value || '').toString();
                  return value.toLowerCase().includes((input || '').toString().toLowerCase());
                }
              }}
              options={catalogProviders.map((p) => ({
                value: p.litellm_provider,
                label: (
                  <Space>
                    {p.icon && <img src={p.icon} alt={p.name} style={{ width: 16, height: 16 }} />}
                    <span>{p.name}</span>
                  </Space>
                ),
              }))}
            />
          </Form.Item>

          <Form.Item
            name="api_key"
            label="API Key"
            rules={[{ required: !editingProvider, message: 'Please enter an API key' }]}
            help={editingProvider ? 'Leave empty to keep existing key' : ''}
          >
            <Input.Password placeholder="Enter your API key" />
          </Form.Item>

          <Form.Item
            name="api_base"
            label="API Base URL (Optional)"
            help="Custom API endpoint URL, if different from default"
          >
            <Input placeholder="https://api.example.com/v1" />
          </Form.Item>

          {editingProvider && (
            <Form.Item name="is_active" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default Vault;
